import * as React from 'react';
import { ICommandBarProps } from '@uifabric/experiments/lib/CommandBar';
import { CommandBar } from '@uifabric/experiments/lib/CommandBar';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { connectToCommandSets } from '../commandSet/CommandSet';
import { ICommand, ICommandStateProps } from '../../models/commands/ICommand';
import { IDecoratedComponentType, IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { concat } from '../../utilities/arrays';
import { Fragment } from '../../utilities/components/Fragment';
import { KeyboardShortcut } from '../keyboard/KeyboardShortcut';
import CommandStrings from '../commands/Commands.resx';

const NO_ITEMS: ReadonlyArray<ICommand> = [];

/**
 * @public
 */
export interface IActionCommandBarOwnProps {
  commands?: ICommand[];
  farCommands?: ICommand[];
  overflowCommands?: ICommand[];
  shortcutCommands?: ICommand[];
  items?: IContextualMenuItem[];
  farItems?: IContextualMenuItem[];
  overflowItems?: IContextualMenuItem[];
  shortcutItems?: IContextualMenuItem[];
}

export type IActionCommandBarInjectedProps = Pick<ICommandBarProps, 'items' | 'farItems' | 'overflowItems' | 'ariaLabel'> &
  Pick<IActionCommandBarOwnProps, 'shortcutItems'>;

/**
 * @public
 */
export type IActionCommandBarProps = IDecoratedProps<ICommandBarProps, IActionCommandBarInjectedProps, IActionCommandBarOwnProps>;

/**
 * Command bar component which accepts 'commands' as input in addition to menu items.
 * The commands are evaluated for availability and behavior and then combined with
 * additional menu items.
 * @public
 */
export const ActionCommandBar: IDecoratedComponentType<ICommandBarProps, IActionCommandBarInjectedProps, IActionCommandBarOwnProps> =
  withDisplayName('ActionCommandBar')(connectToCommandSets((props: IActionCommandBarOwnProps) => {
    return {
      main: props.commands,
      overflow: props.overflowCommands,
      far: props.farCommands,
      shortcut: props.shortcutCommands
    };
  }, (menuItems: {
    main: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
    overflow: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
    far: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
    shortcut: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
  }, props: IActionCommandBarOwnProps): IActionCommandBarInjectedProps => {
      const {
        items = NO_ITEMS,
        overflowItems = NO_ITEMS,
        farItems = NO_ITEMS,
        shortcutItems = NO_ITEMS
      } = props;

      return {
        items: concat(items, menuItems.main) as ICommand[],
        overflowItems: concat(overflowItems, menuItems.overflow) as ICommand[],
        farItems: concat(farItems, menuItems.far) as ICommand[],
        shortcutItems: concat(shortcutItems, menuItems.shortcut) as ICommand[],
        ariaLabel: CommandStrings.CommandBarAriaLabel
      };
    })(withDisplayName('ActionCommandBar.Callback')((props: ICommandBarProps & IActionCommandBarInjectedProps): JSX.Element => {
      const {
        items,
        farItems,
        overflowItems,
        shortcutItems
      } = props;

      const keyboardCommands = getKeyShortcutsCommands([...items, ...farItems, ...overflowItems, ...shortcutItems]);

      return (
        <Fragment>
          <CommandBar
            overflowButtonProps={ { ariaLabel: CommandStrings.OverflowCommandTooltip, title: CommandStrings.OverflowCommandTooltip } }
            { ...props }
          />
          { keyboardCommands && keyboardCommands.length > 0 && keyboardCommands.map((command: IContextualMenuItem & ICommandStateProps) => {
            return (command.keytipProps && command.keytipProps.keySequences.length > 0 ?
              <KeyboardShortcut
                key={ command.key }
                cmdKey={ command.keytipProps.keySequences[0] }
                keySequences={ command.keytipProps.keySequences }
                description={ command.keytipProps.content }
                onExecute={ command.onClick }
              /> : null
            );
          }) }
        </Fragment>
      );
    })));

function getKeyShortcutsCommands(items: (IContextualMenuItem & ICommandStateProps)[]): (IContextualMenuItem & ICommandStateProps)[] {
  return items.filter((item: IContextualMenuItem & ICommandStateProps) => item && !!item.keytipProps);
}
